import {
	CalendarOutlined,
	FileTextOutlined,
	PercentageOutlined,
	WalletOutlined,
} from "@ant-design/icons";
import { Button } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import type { AppRole } from "@/shared/config/navigation";
import { canUseEarlyPayment } from "@/shared/config/permissions";
import type { ClientDetail } from "../types/clients.types";

interface ClientOverviewTabProps {
	client: ClientDetail;
	role: AppRole;
}

export function ClientOverviewTab({ client, role }: ClientOverviewTabProps) {
	const navigate = useNavigate();
	const location = useLocation();
	const sale = client.activeSale;
	const canPayEarly = canUseEarlyPayment(role);

	const saleId = client.salesHistory[0]?.id.replace("#", "sale-");
	const clientQuery = saleId ? `saleId=${saleId}&clientId=${client.id}` : `clientId=${client.id}`;

	const goToContract = () => {
		navigate(`/contract?${clientQuery}`, { state: location.state });
	};

	const goToSchedule = () => {
		if (saleId) {
			navigate(`/sales/${saleId}/schedule?clientId=${client.id}`, { state: location.state });
		}
	};

	const goToPayment = () => {
		navigate(`/payments/new?${clientQuery}`, { state: location.state });
	};

	const goToEarlyPayment = () => {
		navigate(`/payments/early?${clientQuery}`, { state: location.state });
	};

	if (!sale) {
		return (
			<div className="crm-card">
				<div className="crm-card-body" style={{ textAlign: "center", padding: "40px" }}>
					<span className="crm-user-role">Aktiv sotuvlar mavjud emas</span>
				</div>
			</div>
		);
	}

	const getProgressFillColor = (percent: number) => {
		if (percent === 100) {
			return "green";
		}
		if (percent > 20) {
			return "";
		}
		return "amber";
	};

	const getScheduleStatusClass = (status: string) => {
		switch (status) {
			case "paid":
				return "green";
			case "expected":
				return "amber";
			case "delayed":
				return "red";
			default:
				return "slate";
		}
	};

	const getScheduleStatusText = (status: string) => {
		switch (status) {
			case "paid":
				return "to'landi";
			case "expected":
				return "kutilmoqda";
			case "delayed":
				return "kechikkan";
			default:
				return "rejada";
		}
	};

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
			{/* Active Sale Card */}
			<div className="crm-card">
				<div
					className="crm-card-head"
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
					}}
				>
					<h3 className="font-bold">Aktiv sotuv</h3>
					<span className="crm-badge blue">Installment</span>
				</div>
				<div className="crm-card-body">
					<div className="crm-user-role" style={{ fontSize: "13px" }}>
						{sale.buildingName} / {sale.blockName} / {sale.floor}-qavat / xona {sale.roomNumber} /{" "}
						{sale.area} m2
					</div>

					<div className="crm-client-kpi-grid">
						<div className="crm-kpi compact">
							<div className="crm-kpi-title">
								<span className="crm-kpi-icon green">
									<WalletOutlined />
								</span>
								Yakuniy narx
							</div>
							<div className="crm-kpi-value">
								<strong>{sale.finalPrice}</strong>
							</div>
							<div className="crm-kpi-note">{sale.discountInfo ?? "shartnoma qiymati"}</div>
						</div>
						<div className="crm-kpi compact">
							<div className="crm-kpi-title">
								<span className="crm-kpi-icon sky">
									<PercentageOutlined />
								</span>
								To'langan
							</div>
							<div className="crm-kpi-value">
								<strong>{sale.paidPercent}</strong>
								<span>%</span>
							</div>
							<div className="crm-kpi-note">
								{sale.paidAmount} / {sale.finalPrice}
							</div>
						</div>
						<div className="crm-kpi compact">
							<div className="crm-kpi-title">
								<span className="crm-kpi-icon violet">
									<CalendarOutlined />
								</span>
								Oylik to'lov
							</div>
							<div className="crm-kpi-value">
								<strong>{sale.monthlyPayment}</strong>
							</div>
							<div className="crm-kpi-note">{sale.monthsCount} oy davomida</div>
						</div>
					</div>

					<div className="crm-mt" style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
						<div className="crm-prog">
							<div
								className={`crm-prog-fill ${getProgressFillColor(sale.paidPercent)}`}
								style={{ width: `${sale.paidPercent}%` }}
							/>
						</div>
					</div>

					<div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "16px" }}>
						<span className="crm-badge blue" style={{ background: "var(--crm-surface-2)" }}>
							Qoldiq: {client.paymentType === "naqd" ? "0 UZS" : "539 500 000 UZS"}
						</span>
						{sale.debtCount > 0 && <span className="crm-badge red">{sale.debtCount} ta qarz</span>}
						{sale.nextPaymentDate !== "Tugallangan" && (
							<span className="crm-badge amber">Keyingi: {sale.nextPaymentDate}</span>
						)}
					</div>

					<div
						className="crm-divider"
						style={{ margin: "16px 0", borderTop: "1px solid var(--crm-border)" }}
					/>

					<div className="crm-client-sale-actions">
						<Button icon={<FileTextOutlined />} onClick={goToContract}>
							Shartnoma
						</Button>
						<Button icon={<CalendarOutlined />} onClick={goToSchedule}>
							Grafik
						</Button>
						<Button className="crm-primary-btn" icon={<WalletOutlined />} onClick={goToPayment}>
							To'lov
						</Button>
						{canPayEarly ? (
							<Button icon={<PercentageOutlined />} onClick={goToEarlyPayment}>
								Erta to'lov
							</Button>
						) : null}
					</div>
				</div>
			</div>

			{/* Payment Schedule Card */}
			<div className="crm-card crm-schedule-table-card" id="payment-schedule-card">
				<div className="crm-card-head">
					<div>
						<h3 className="font-bold">To'lov grafigi</h3>
						<p>{sale.monthsCount} oy</p>
					</div>
				</div>
				<div className="crm-table-wrap">
					<table className="crm-table">
						<thead>
							<tr>
								<th>№</th>
								<th>Sana</th>
								<th>Summa</th>
								<th>Holat</th>
								<th aria-label="Action" />
							</tr>
						</thead>
						<tbody>
							{client.schedule.map((item) => (
								<tr key={item.index}>
									<td>
										<strong className="font-bold">{item.index}</strong>
									</td>
									<td data-label="Sana">{item.date}</td>
									<td data-label="Summa">{item.amount}</td>
									<td data-label="Holat">
										<span className={`crm-badge ${getScheduleStatusClass(item.status)}`}>
											{getScheduleStatusText(item.status)}
										</span>
									</td>
									<td>
										{item.status === "expected" || item.status === "delayed" ? (
											<Button size="small" type="primary" onClick={goToPayment}>
												To'lash
											</Button>
										) : (
											<span className="crm-user-role">-</span>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
				<div className="crm-table-foot">
					<span>
						{client.schedule.filter((x) => x.status === "paid").length} / {sale.monthsCount}{" "}
						to'langan · qoldiq {client.paymentType === "naqd" ? "0 UZS" : "539 500 000 UZS"}
					</span>
				</div>
			</div>
		</div>
	);
}
